import { orgSelectors } from 'ducks/orgs'
import { paymentOptionSelectionSelectors } from 'ducks/paymentOptionSelection'
import { projectMilestonesSelectors } from 'ducks/projectMilestones'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { StudioSystemType } from 'types/global'
import { AvailableFinanceIntegrationType } from 'types/orgs'
import { PaymentOptionDataType } from 'types/paymentOptions'
import { ProposalDataType } from 'types/proposals'
import { MESSAGE_TYPE_PRIORITY } from './paymentOptionSelection/constants'
import { CalculatedPaymentOptionMessageType } from './paymentOptionSelection/types'
import { ResolvedSystem, resolveSystem } from './resolveSystem'

export const useGetIntegration = (integrationName: string | null | undefined) => {
  const [integration, setIntegration] = useState<AvailableFinanceIntegrationType | undefined>(undefined)

  const availableIntegrations = useSelector(orgSelectors.getAvailableFinanceIntegrations)

  useEffect(() => {
    if (availableIntegrations?.length && integrationName) {
      let foundIntegration = availableIntegrations?.find(
        (integration) => integration.integration_name === integrationName
      )
      setIntegration(foundIntegration)
    }
  }, [availableIntegrations, integrationName])

  return integration
}

const STATUS_SORTING_MAP = {
  active: 2,
  shared: 1,
}

export const useGetSortedIntegrations = (
  onlyResidential: boolean,
  onlyCommercial: boolean,
  onlyInactive: boolean,
  onlyActive: boolean
) => {
  const [sortedIntegrations, setSortedIntegrations] = useState<AvailableFinanceIntegrationType[]>()

  const availableIntegrations = useSelector(orgSelectors.getAvailableFinanceIntegrations)
  const org = useSelector(orgSelectors.getOrg)

  useEffect(() => {
    if (availableIntegrations) {
      let integrationsToDisplay = availableIntegrations?.filter(
        (integration) =>
          !integration.banner_content?.hide_from_payment_option_selection_dialog &&
          ((integration.support_residential && onlyResidential) || (integration.support_commercial && onlyCommercial))
      )
      let sortedIntegrations = integrationsToDisplay
        .filter((integration) => {
          if (!onlyInactive && !onlyActive) return true
          // filter based on status
          if (onlyInactive && onlyActive) {
            console.warn('Both active and inactive integrations are being filtered out')
            return false
          }
          let status = STATUS_SORTING_MAP[org?.[`enable_${integration.integration_name}`]] || 0
          if (onlyInactive) {
            return !status
          } else if (onlyActive) {
            return !!status
          }
          return true
        })
        .sort((a, b) => {
          let aStatus = STATUS_SORTING_MAP[org?.[`enable_${a.integration_name}`]] || 0
          let bStatus = STATUS_SORTING_MAP[org?.[`enable_${b.integration_name}`]] || 0
          return bStatus - aStatus
        })
        ?.sort((a, b) => {
          let aPriority = a?.banner_content?.priority || 99
          let bPriority = b?.banner_content?.priority || 99
          return aPriority - bPriority
        })
      setSortedIntegrations(sortedIntegrations)
    }
  }, [availableIntegrations])

  return sortedIntegrations
}

export const useGetMostUrgentMessage = (messages: CalculatedPaymentOptionMessageType[] | undefined) => {
  const [messageToShow, setMessageToShow] = useState<CalculatedPaymentOptionMessageType | null>(null)

  useEffect(() => {
    // only show one message at a time, sort by priority and pick the first one
    if (messages?.length) {
      let sortedMessages = messages?.sort((a, b) => {
        let aPriority = MESSAGE_TYPE_PRIORITY[a.type] || 3
        let bPriority = MESSAGE_TYPE_PRIORITY[b.type] || 3
        return aPriority - bPriority
      })
      setMessageToShow(sortedMessages[0])
    } else if (messageToShow) {
      setMessageToShow(null)
    }
  }, [messages])

  return messageToShow
}

export const useGetMonthlyBillSavings = (system: StudioSystemType | undefined) => {
  const [billSavings, setBillSavings] = useState<number>(0)

  useEffect(() => {
    if (system && system.bills) {
      // `bills.current` / `bills.proposed` may exist but lack the `bills_yearly`
      // array — e.g. on the non-solar Quote page's placeholder system, the calc
      // pipeline only populates partial bills (`bill_frequency`, `incentives`).
      // Without optional chaining on `bills_yearly`, accessing `[0]` on
      // `undefined` throws synchronously inside the effect, which bubbles up
      // to the nearest error boundary and takes the whole project page down
      // when the Add Payment Options dialog mounts.
      const currentAnnual = system?.bills?.current?.bills_yearly?.[0]?.annual?.total || 0
      const proposed = system?.bills?.proposed
      const firstProposed = proposed ? Object.values(proposed)[0] : undefined
      const proposalAnnual = firstProposed?.bills_yearly?.[0]?.annual?.total || 0
      setBillSavings((currentAnnual - proposalAnnual) / 12)
    }
  }, [])

  return billSavings
}

export const useGetCurrentAverageBill = (system: StudioSystemType | undefined) => {
  const [averageBill, setAverageBill] = useState<number>(0)

  useEffect(() => {
    if (system && system.bills) {
      // Same defensive optional chaining as ``useGetMonthlyBillSavings`` —
      // ``bills.current`` may exist with a missing ``bills_yearly`` slot on
      // Studio-less / partially-calc'd systems.
      const currentAnnual = system?.bills?.current?.bills_yearly?.[0]?.annual?.total || 0
      setAverageBill(currentAnnual / 12)
    }
  }, [])

  return averageBill
}

export const useGetMilestones = (
  pmtId: number | undefined,
  systemUuid: string | undefined | null,
  projectId: number,
  proposalData?: ProposalDataType
) => {
  // NOTE - this gets milestones directly from calcs saved on the system and can therefore be stale. Only use this function
  // if you're only interested in the payment amounts. Use useGetPaymentRequets if having updated milestone status values is important
  const milestoneRefreshTrigger = useSelector(projectMilestonesSelectors.getRefreshMilestonesTrigger)
  const systemRereshTriger = useSelector(paymentOptionSelectionSelectors.getSystemRefreshTrigger)

  const milestones = useMemo(() => {
    if (!pmtId || !systemUuid) return undefined
    // Prefer the in-editor system, fall back to proposalData when studio is
    // hidden (use_prerendered_system_image) so customer-facing callers still resolve.
    const thisSystem = resolveSystem(systemUuid, proposalData)

    if (thisSystem && pmtId) {
      const expectedMilestones = thisSystem?.payment_options?.find(
        (pmt) => pmt.id === pmtId
      )?.expected_milestone_payments

      if (expectedMilestones?.length) return expectedMilestones
    }
    return undefined
  }, [pmtId, systemUuid, proposalData, milestoneRefreshTrigger, systemRereshTriger])

  return milestones
}

// Type overloads exposed below as `useSystem`: callers that don't pass `proposalData`
// (almost all of which are pro flows that read studio-specific methods like
// moduleType()) keep the StudioSystemType return. Customer-facing callers that pass
// `proposalData` opt in to the broader union return so they correctly handle the
// studio-hidden fallback.
function useSystemImpl(
  systemUuid: string | null,
  proposalData?: ProposalDataType,
  options?: { refreshOnSystemsTrigger?: boolean }
): ResolvedSystem | undefined {
  // Pass `proposalData` from customer-facing callers so studio-hidden flows
  // (use_prerendered_system_image) can fall back to the proposal/API system shape.
  const refreshSystemsTrigger = useSelector(paymentOptionSelectionSelectors.getSystemRefreshTrigger)
  return useMemo(
    () => resolveSystem(systemUuid, proposalData),
    [systemUuid, proposalData, options?.refreshOnSystemsTrigger ? refreshSystemsTrigger : null]
  )
}

export const useSystem: {
  (systemUuid: string | null): StudioSystemType | undefined
  (
    systemUuid: string | null,
    proposalData: ProposalDataType | undefined,
    options?: { refreshOnSystemsTrigger?: boolean }
  ): ResolvedSystem | undefined
} = useSystemImpl as any

export const useIsProjectUsingCashFlow = (
  soldSystem: StudioSystemType | undefined,
  soldPaymentOption: PaymentOptionDataType | undefined,
  refreshSystemsTrigger: string | undefined,
  allSystems: StudioSystemType[] = []
) => {
  const fetchSystems = () => {
    if (allSystems?.length) return allSystems
    const systemsFromEditor = window.editor?.getSystems() || []
    return systemsFromEditor
  }
  const [systems, setSystems] = useState<StudioSystemType[]>(fetchSystems())

  useEffect(() => {
    if (refreshSystemsTrigger) {
      setSystems([...fetchSystems()])
    }
  }, [refreshSystemsTrigger])

  useEffect(() => {
    if (allSystems?.length && !systems?.length) {
      setSystems([...allSystems])
    }
  }, [systems, allSystems])

  const selectedSystem =
    soldSystem || systems.find((s) => s.payment_options?.some((p) => p.expected_milestone_payments?.length))

  const selectedPaymentOption =
    soldPaymentOption || selectedSystem?.payment_options?.find((p) => p.expected_milestone_payments?.length)

  const isProjectUsingCashflow = useMemo(
    () => !!selectedPaymentOption?.expected_milestone_payments?.length,
    [selectedPaymentOption?.expected_milestone_payments]
  )

  return isProjectUsingCashflow
}
